import React, { useState } from 'react';
import Button from '../common/Button';
import BoardCard from '../boards/BoardCard';

const StyleGuide = () => {
  const [selectedTheme, setSelectedTheme] = useState('retro');

  // Mock board data for demonstration
  const mockBoard = {
    id: 1,
    name: 'Kilter Board',
    type: 'kilter',
    description: 'State-of-the-art adjustable training board',
    location_notes: 'Main Training Area',
    angle: 40,
    is_adjustable_angle: false,
    hold_sets: ['Kilter 2023', 'Kilter 2024'],
    features: {
      led: true,
      fan: true,
      mirrored: false
    },
    is_featured: true,
    show_new_badge: false,
    image: null
  };

  const colorPalettes = {
    retro: {
      name: 'Retro Modern',
      description: 'Jazz cup meets PDX carpet - vibrant yet professional',
      colors: [
        { name: 'Teal', shades: ['teal-500', 'teal-600', 'teal-700'] },
        { name: 'Magenta', shades: ['magenta-500', 'magenta-600', 'magenta-700'] },
        { name: 'Coral', shades: ['coral-500', 'coral-600', 'coral-700'] },
        { name: 'Purple', shades: ['purple-500', 'purple-600', 'purple-700'] },
        { name: 'Sunshine', shades: ['sunshine-500', 'sunshine-600', 'sunshine-700'] },
      ]
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-slate-900 mb-4 bg-gradient-to-r from-teal-600 via-purple-600 to-magenta-600 bg-clip-text text-transparent">
            BoardRez Design System
          </h1>
          <p className="text-xl text-slate-600">
            A retro-modern aesthetic for the modern climbing gym
          </p>
        </div>

        {/* Color Palette Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center">
            <span className="w-2 h-8 bg-gradient-to-b from-teal-500 to-magenta-500 mr-3 rounded"></span>
            Color Palette
          </h2>
          <div className="bg-white rounded-xl p-8 shadow-retro border-2 border-slate-900">
            <p className="text-slate-600 mb-6">{colorPalettes.retro.description}</p>

            <div className="space-y-6">
              {colorPalettes.retro.colors.map((colorGroup) => (
                <div key={colorGroup.name}>
                  <h3 className="text-lg font-semibold text-slate-700 mb-3">{colorGroup.name}</h3>
                  <div className="flex gap-4">
                    {colorGroup.shades.map((shade) => {
                      const [color, number] = shade.split('-');
                      return (
                        <div key={shade} className="flex-1">
                          <div className={`h-24 rounded-lg bg-${shade} border-2 border-slate-900 shadow-retro transition-transform hover:scale-105`}></div>
                          <p className="text-sm text-slate-600 mt-2 text-center font-mono">{shade}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Typography Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center">
            <span className="w-2 h-8 bg-gradient-to-b from-coral-500 to-sunshine-500 mr-3 rounded"></span>
            Typography
          </h2>
          <div className="bg-white rounded-xl p-8 shadow-retro border-2 border-slate-900 space-y-4">
            <h1 className="text-5xl font-bold text-slate-900">Heading 1</h1>
            <h2 className="text-4xl font-bold text-slate-800">Heading 2</h2>
            <h3 className="text-3xl font-semibold text-slate-800">Heading 3</h3>
            <h4 className="text-2xl font-semibold text-slate-700">Heading 4</h4>
            <p className="text-lg text-slate-600">
              Body text - The quick brown fox jumps over the lazy dog. This is what your regular content will look like throughout the application.
            </p>
            <p className="text-sm text-slate-500">
              Small text - Perfect for captions, labels, and supporting information.
            </p>
          </div>
        </section>

        {/* Buttons Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center">
            <span className="w-2 h-8 bg-gradient-to-b from-purple-500 to-teal-500 mr-3 rounded"></span>
            Buttons
          </h2>
          <div className="bg-white rounded-xl p-8 shadow-retro border-2 border-slate-900">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Retro-styled buttons */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Retro Primary</h4>
                <button className="w-full bg-teal-500 hover:bg-teal-600 text-white font-bold py-3 px-6 rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all">
                  Reserve Now
                </button>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Retro Magenta</h4>
                <button className="w-full bg-magenta-500 hover:bg-magenta-600 text-white font-bold py-3 px-6 rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all">
                  Featured Action
                </button>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Retro Coral</h4>
                <button className="w-full bg-coral-500 hover:bg-coral-600 text-white font-bold py-3 px-6 rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all">
                  Cancel
                </button>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Retro Outline</h4>
                <button className="w-full bg-white hover:bg-teal-50 text-teal-600 font-bold py-3 px-6 rounded-lg border-2 border-teal-500 shadow-retro hover:shadow-retro-hover transition-all">
                  View Details
                </button>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Gradient Fun</h4>
                <button className="w-full bg-gradient-to-r from-teal-500 via-purple-500 to-magenta-500 hover:from-teal-600 hover:via-purple-600 hover:to-magenta-600 text-white font-bold py-3 px-6 rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all">
                  Special Offer
                </button>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Soft Modern</h4>
                <button className="w-full bg-purple-500 hover:bg-purple-600 text-white font-semibold py-3 px-6 rounded-lg shadow-soft hover:shadow-lg transition-all">
                  Soft Shadow
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Cards & Badges Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center">
            <span className="w-2 h-8 bg-gradient-to-b from-sunshine-500 to-coral-500 mr-3 rounded"></span>
            Cards & Badges
          </h2>

          {/* Badge Examples */}
          <div className="bg-white rounded-xl p-8 shadow-retro border-2 border-slate-900 mb-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Status Badges</h3>
            <div className="flex flex-wrap gap-3">
              <span className="px-4 py-2 bg-teal-100 text-teal-800 border-2 border-teal-500 rounded-full font-semibold text-sm">
                Available
              </span>
              <span className="px-4 py-2 bg-coral-100 text-coral-800 border-2 border-coral-500 rounded-full font-semibold text-sm">
                Occupied
              </span>
              <span className="px-4 py-2 bg-slate-100 text-slate-800 border-2 border-slate-400 rounded-full font-semibold text-sm">
                Closed
              </span>
              <span className="px-4 py-2 bg-magenta-100 text-magenta-800 border-2 border-magenta-500 rounded-full font-semibold text-sm">
                Featured
              </span>
              <span className="px-4 py-2 bg-purple-100 text-purple-800 border-2 border-purple-500 rounded-full font-semibold text-sm">
                Premium
              </span>
              <span className="px-4 py-2 bg-sunshine-100 text-sunshine-800 border-2 border-sunshine-500 rounded-full font-semibold text-sm">
                New
              </span>
            </div>
          </div>

          {/* Pattern Examples */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-xl p-8 shadow-retro border-2 border-slate-900">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Retro Card Style</h3>
              <p className="text-slate-600">
                Bold borders and drop shadows create that classic 80s/90s aesthetic while maintaining modern usability.
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-50 via-purple-50 to-magenta-50 rounded-xl p-8 shadow-soft border border-slate-200">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Gradient Soft Style</h3>
              <p className="text-slate-600">
                Subtle gradients with soft shadows for a more contemporary, gentle appearance.
              </p>
            </div>
          </div>

          {/* Geometric Patterns */}
          <div className="bg-white rounded-xl p-8 shadow-retro border-2 border-slate-900">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Geometric Accents (PDX-inspired)</h3>
            <div className="flex gap-4 items-center">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-teal-700 rounded-lg border-2 border-slate-900 shadow-retro"></div>
              <div className="w-20 h-20 bg-gradient-to-br from-magenta-500 to-magenta-700 transform rotate-45 border-2 border-slate-900 shadow-retro"></div>
              <div className="w-20 h-20 bg-gradient-to-br from-coral-500 to-coral-700 rounded-full border-2 border-slate-900 shadow-retro"></div>
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-700 border-2 border-slate-900 shadow-retro"></div>
              <div className="flex-1 h-4 bg-gradient-to-r from-teal-500 via-purple-500 to-magenta-500 rounded-full border-2 border-slate-900"></div>
            </div>
          </div>
        </section>

        {/* Board Card Example */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center">
            <span className="w-2 h-8 bg-gradient-to-b from-magenta-500 to-coral-500 mr-3 rounded"></span>
            Board Card Example
          </h2>
          <div className="max-w-md mx-auto">
            <BoardCard
              board={mockBoard}
              onReserve={() => alert('Reservation clicked!')}
              showReserveButton={true}
              showViewButton={true}
            />
          </div>
        </section>

        {/* Design Principles */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center">
            <span className="w-2 h-8 bg-gradient-to-b from-teal-500 via-purple-500 to-magenta-500 mr-3 rounded"></span>
            Design Principles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-retro border-2 border-slate-900">
              <div className="text-4xl mb-4">🎨</div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Vibrant Yet Professional</h3>
              <p className="text-slate-600">
                Bold colors inspired by 80s/90s design, balanced with modern restraint for professionalism.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-retro border-2 border-slate-900">
              <div className="text-4xl mb-4">⚡</div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Energetic & Dynamic</h3>
              <p className="text-slate-600">
                Geometric shapes, gradients, and bold shadows create movement and excitement.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-retro border-2 border-slate-900">
              <div className="text-4xl mb-4">🎯</div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Function First</h3>
              <p className="text-slate-600">
                Style never compromises usability - all elements maintain excellent accessibility.
              </p>
            </div>
          </div>
        </section>

        {/* Usage Notes */}
        <section className="bg-gradient-to-r from-teal-500 via-purple-500 to-magenta-500 rounded-xl p-8 border-2 border-slate-900 shadow-retro">
          <h2 className="text-3xl font-bold text-white mb-4">How to Use This Design System</h2>
          <div className="bg-white bg-opacity-90 rounded-lg p-6 space-y-3 text-slate-700">
            <p className="font-semibold">Color Guidelines:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Teal (500-600)</strong> - Primary actions, main CTAs, available states</li>
              <li><strong>Magenta (500-600)</strong> - Featured items, premium features, highlights</li>
              <li><strong>Coral (500-600)</strong> - Warnings, occupied states, cancel actions</li>
              <li><strong>Purple (500-600)</strong> - Secondary actions, special features</li>
              <li><strong>Sunshine (500-600)</strong> - Accents, new badges, positive highlights</li>
              <li><strong>Slate (various)</strong> - Text, borders, neutral elements</li>
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
};

export default StyleGuide;
